import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Database, LogOut, ArrowUpRight, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtMoney, fmtDate, today } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Badge2, Bar, Kpi, PageHead } from "@/components/Bits";
import { Logo } from "@/components/Logo";
import { TrialBanner } from "@/components/TrialGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFormDraft } from "@/lib/drafts";
import { DraftPrompt, DraftRestoredBanner } from "@/components/DraftPrompt";

const BLANK = {
  name: "", client: "", location: "", industry: "thermal", contract_value: 0,
  start_date: today(), finish_date: today(), description: "",
};

const INDUSTRIES = [
  ["thermal", "Thermal Power Plant"],
  ["solar", "Solar Power Plant"],
  ["refinery", "Refinery & Petrochemical"],
  ["nuclear", "Nuclear Facility"],
  ["infrastructure", "Infrastructure / Process Plant"],
];

export default function Projects() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const draft = useFormDraft({
    formId: "project:new", form, setForm, blank: BLANK, onRestore: () => setOpen(true),
  });

  const load = () => {
    setLoading(true);
    api.get("/projects").then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const seed = async () => {
    try {
      const { data } = await api.post("/seed/demo");
      toast.success(data.seeded ? `Loaded ${data.projects.length} live demo projects` : data.message);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const create = async () => {
    try {
      const { data } = await api.post("/projects", { ...form, contract_value: Number(form.contract_value) });
      await api.post(`/projects/${data.id}/apply-template?template=${form.industry}`);
      toast.success("Project created with pre-loaded industry WBS");
      setOpen(false);
      draft.clear();
      navigate(`/p/${data.id}/dashboard`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen">
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sticky top-0 z-20 bg-background/85 backdrop-blur-md">
        <Logo size="sm" />
        <span className="hidden md:inline text-[11px] text-muted-foreground border-l border-border pl-3" data-testid="portfolio-tagline">
          Plan Smarter. Build Stronger. Deliver on Time.
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/about" className="hidden sm:inline text-[12px] text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="portfolio-about-link">About</Link>
          <Link to="/pricing" className="text-[12px] text-[#F97316] hover:underline" data-testid="portfolio-pricing-link">Plans &amp; Billing</Link>
          <Link to="/om/dashboard" className="text-[12px] text-[#C9A84C] hover:underline font-medium" data-testid="portfolio-om-link">O&amp;M</Link>
          {user?.role === "superadmin" ? (
            <Link to="/admin/data-editor" className="text-[12px] text-[#F97316] hover:underline" data-testid="portfolio-data-editor-link">Data editor</Link>
          ) : null}
          <span className="text-[13px] text-muted-foreground hidden sm:inline">{user?.name}</span>
          <Badge2 tone="primary">{user?.role}</Badge2>
          <Link to="/settings" className="inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="portfolio-settings-link">
            <ShieldCheck className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Security</span>
          </Link>
          <ThemeToggle />
          <button onClick={logout} className="p-1.5 hover:bg-muted rounded" data-testid="logout-btn">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div className="p-4 sm:p-6 max-w-[1600px]">
        <TrialBanner />
        <DraftPrompt draft={draft} label="project" />
        <PageHead title="Project Portfolio" subtitle="Every EPC package, one execution platform.">
          <Button variant="outline" onClick={seed} data-testid="seed-demo-btn">
            <Database className="w-4 h-4 mr-1.5" /> Load demo portfolio
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-project-btn"><Plus className="w-4 h-4 mr-1.5" /> New project</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New project</DialogTitle></DialogHeader>
              <DraftRestoredBanner draft={draft} />
              <div className="grid sm:grid-cols-2 gap-3">
                {[["name", "Project name"], ["client", "Client"], ["location", "Location"], ["contract_value", "Contract value (₹)"]].map(([k, l]) => (
                  <div key={k} className={k === "name" ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                    <Label className="text-xs uppercase tracking-wider">{l}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`project-${k}-input`} />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Industry template</Label>
                  <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                    <SelectTrigger data-testid="project-industry-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Start date</Label>
                  <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="project-start-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider">Finish date</Label>
                  <Input type="date" value={form.finish_date} onChange={(e) => setForm({ ...form, finish_date: e.target.value })} data-testid="project-finish-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={!form.name} data-testid="project-create-submit-btn">Create & load WBS</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PageHead>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading portfolio…</div>
        ) : rows.length === 0 ? (
          <div className="border border-dashed border-border p-10 text-center">
            <p className="text-sm text-muted-foreground">No projects yet. Load the demo portfolio to explore a fully populated thermal, solar, refinery and nuclear package.</p>
            <Button className="mt-4" onClick={seed} data-testid="seed-demo-empty-btn">
              <Database className="w-4 h-4 mr-1.5" /> Load demo portfolio
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Kpi label="Active packages" value={rows.length} testId="kpi-project-count" />
              <Kpi label="Portfolio value" value={fmtMoney(rows.reduce((s, r) => s + Number(r.contract_value || 0), 0))} tone="primary" />
              <Kpi label="Avg physical progress" value={`${(rows.reduce((s, r) => s + (r.percent_complete || 0), 0) / rows.length).toFixed(1)}%`} />
              <Kpi label="Activities tracked" value={rows.reduce((s, r) => s + (r.activity_count || 0), 0)} />
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
              {rows.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/p/${p.id}/dashboard`)}
                  className="text-left border border-border bg-card p-4 hover:border-primary transition-colors duration-150 rise group"
                  data-testid={`project-card-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <Badge2 tone="primary">{p.industry}</Badge2>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-150" />
                  </div>
                  <h3 className="font-semibold mt-2.5 leading-snug">{p.name}</h3>
                  <p className="text-[12px] text-muted-foreground mt-0.5">{p.client} · {p.location}</p>
                  <div className="mt-3"><Bar pct={p.percent_complete} /></div>
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border">
                    {[["SPI", p.spi, p.spi >= 0.95], ["CPI", p.cpi, p.cpi >= 1], ["Value", fmtMoney(p.contract_value), true]].map(([l, v, ok]) => (
                      <div key={l}>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                        <div className={`num text-[13px] font-semibold ${ok ? "text-emerald-500" : "text-red-500"}`}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2">
                    {fmtDate(p.start_date)} → {fmtDate(p.finish_date)}
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
